import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import useSEOPage from '../hooks/useSEOPage';
import { publicApi } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import StructuredData, { buildFAQSchema } from '../components/StructuredData';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { seoData } = useSEOPage('faq');
  const { t, tc, language } = useLanguage();

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const params = language !== 'fr' ? `?lang=${language}` : '';
        const { data } = await publicApi.get(`/faqs${params}`);
        
        const grouped = data.reduce((acc, faq) => {
          const category = faq.category || 'Général';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push({ q: faq.question, a: faq.answer });
          return acc;
        }, {});
        
        const formatted = Object.keys(grouped).map(category => ({
          category,
          questions: grouped[category]
        }));
        
        setFaqData(formatted);
      } catch (err) {
        console.error('Error loading FAQs:', err);
        setFaqData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFAQs();
  }, [language]);

  return (
    <>
      {seoData && (
        <Helmet>
          <title>{seoData.meta_title || seoData.title || 'FAQ - Questions fréquentes | Joey Cyan'}</title>
          <meta name="description" content={seoData.meta_description || seoData.description || 'Retrouvez les réponses aux questions les plus fréquentes sur nos lunettes connectées Joey Cyan.'} />
          {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}
          {seoData.og_image && <meta property="og:image" content={seoData.og_image} />}
        </Helmet>
      )}
      <StructuredData
        data={buildFAQSchema(
          faqData.flatMap((cat) => cat.questions.map((it) => ({ question: it.q, answer: it.a })))
        )}
      />
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="faq-page">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <div className="text-center mb-10">
          <p className="text-xs text-[#00aaff] uppercase tracking-widest font-semibold mb-2">Support</p>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">{t('faq.title')}</h1>
          <p className="text-gray-500">{t('faq.subtitle')}</p>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : faqData.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">{t('faq.noFaq')}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {faqData.map((section, sectionIndex) => (
            <div key={section.category}>
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <HelpCircle size={20} className="text-[#00aaff]" />
                {tc(section.category)}
              </h2>
              <div className="space-y-2">
                {section.questions.map((item, qIndex) => {
                  const index = `${sectionIndex}-${qIndex}`;
                  const isOpen = openIndex === index;
                  return (
                    <div key={index} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : index)}
                        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-medium text-gray-900">{item.q}</span>
                        {isOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-4">
                          <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        )}

        <div className="mt-12 text-center bg-white rounded-2xl border border-gray-100 p-8">
          <h3 className="font-bold text-gray-900 mb-2">{t('faq.stillQuestions')}</h3>
          <p className="text-gray-500 text-sm mb-4">{t('faq.subtitle')}</p>
          <a href="mailto:support@joeycyan.com" className="inline-flex items-center gap-2 bg-[#00aaff] text-white px-6 py-3 rounded-full font-bold hover:bg-[#0088dd] transition-colors">
            {t('faq.contactUs')}
          </a>
        </div>
      </div>
    </div>
    </>
  );
}
